/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
  observable,
  freeze,
}                                              from 'rewire-core';
import DataMutations                           from '../graphql/Mutations';
import { confirmationModel }                   from '../ui/ConfirmationDialog';
import { notifications }                       from '../ui/Notifications';
import { timecardTestRouteManager }            from '../main-navigation/RouteManager';
import mainPageModel                           from '../main-navigation/MainPageModel';
import addTimecardTestDialogModel              from './AddTimecardTestDialogModel';
import timecardTestSummaryModel, {
  TimecardTestSummaryModel
}                                              from './TimecardTestSummaryModel';
import timecardTestEditTimecardsModel, {
  TimecardTestEditTimecardsModel
}                                              from './editTimecard/TimecardTestEditTimecardsModel';
import timecardTestListModel                   from './TimecardTestListModel';

export type TestViewType = 'Summary' | 'Edit Timecard'

interface ITimecardTestState {
  testId?:   string;
  view:      TestViewType;
  isLoading: boolean;
}

export class TimecardTestModel {
  state: ITimecardTestState;
 
  constructor() {
    this.state = observable({
      testId:    undefined,
      isLoading: false,
      view:     'Summary',
    });
  }

  reset() {
    freeze(() => {
      this.isLoading = false;
      this.testId    = undefined;
    });
  }

  async load(testId?: string, view?: TestViewType) {
    this.reset();
    this.isLoading = true;
    try {
      this.testId = testId;
      if (view) {
        this.state.view = view;
      }
      await this.loadView();
    } finally {
      this.isLoading = false;
    }
  }

  loadView = async () => {
    if (!this.testId) {
      return;
    }

    const viewModel = this.currentViewModel;
    await viewModel.load(this.testId);
  };

  getViewModel(view: TestViewType): TimecardTestSummaryModel | TimecardTestEditTimecardsModel {
    if (view === 'Summary') {
      return timecardTestSummaryModel;
    } else {
      return timecardTestEditTimecardsModel;
    }
  }

  get currentViewModel(): TimecardTestSummaryModel | TimecardTestEditTimecardsModel {
    return this.getViewModel(this.view);
  }

  get testId(): string | undefined {
    return this.state.testId;
  }

  set testId(value: string | undefined) {
    this.state.testId = value;
  }

  get view(): TestViewType {
    return this.state.view;
  }

  set view (value: TestViewType) {
    if (this.view === value) {
      return;
    }

    timecardTestRouteManager.navigateTo({ testView: value });
  }

  get isLoading(): boolean {
    return this.state.isLoading || timecardTestRouteManager.isLoading;
  }

  set isLoading(value: boolean) {
    this.state.isLoading = value;
  }

  get hasTest(): boolean {
    return !!(timecardTestListModel.selectedTest && this.testId);
  }

  isView(view: TestViewType): boolean {
    return this.state.view === view;
  }

  createNewTimecardTest() {
    addTimecardTestDialogModel.load();
  }

  async deleteTimecardTest() {
    if (!this.canDelete()) {
      return;
    }
    this.confirmDeleteTimecardTest(this.testId!);
  }

  confirmDeleteTimecardTest(testId: string) {
    confirmationModel.load(
      'Are you sure you want to delete this timecard test?',
      {
        yesAction: () => {
          this.executeDeleteTimecardTest(testId);
          return true;
        },
        title: 'Confirm Timecard Test Deletion',
        maxWidth: 'sm'
      }
    );
  }

  executeDeleteTimecardTest = async (testId: string) => {
    const result = await DataMutations.deleteTimecardTests([testId]);
    if (result) {
      notifications.success('Timecard Test Deleted');
      timecardTestListModel.navigateTo();
    } else {
      notifications.error('Timecard Test Deletion Failed');
    }
  };

  cancel() {
    this.currentViewModel.cancel();
  }

  canDelete(): boolean {
    return !!this.testId && mainPageModel.canEditTests;
  }

  canAdd(): boolean {
    return mainPageModel.canEditTests;
  }

  canUpdate(): boolean {
    return this.canSave() && mainPageModel.canEditTests;
  }

  canRunTests(): boolean {
    return mainPageModel.canEditTests;
  }

  canRunRules(): boolean {
    return timecardTestEditTimecardsModel.canRunRules();
  }

  async runRules() {
    await timecardTestEditTimecardsModel.runRules();
  }

  canSave(): boolean {
    return this.currentViewModel.canSave();
  }

  async save() {
    await this.currentViewModel.save();
  }
}

const timecardTestModel = new TimecardTestModel();
export default timecardTestModel;